import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Handshake, Building2, User as UserIcon, Calendar, ChevronRight, ChevronLeft,
  CheckCircle, Loader2, Download, Plus, Trash2, Sparkles,
  History, Save, AlertCircle, Lock, ExternalLink, Eye, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { successAgreementAPI, profileAPI } from "../../services/api";
import ProfileSyncModal from "./ProfileSyncModal";
import useAutosave from "../../hooks/useAutosave";
import AutosaveIndicator from "./AutosaveIndicator";

// SuccessAgreementBuilder — Iter-37.1
// 5-step wizard with:
//   • Profile cascade (locked email/phone + cascading name/location)
//   • 3-way sync modal on profile-cascade overrides (mirrors Resume / CL)
//   • Step 5 live PDF preview iframe + Open-in-new-tab fallback

const TERM_OPTIONS = [1.0, 1.5, 2.0, 2.5, 3.0];
const STEPS = [
  { id: 1, label: "Parties & Term", icon: Building2 },
  { id: 2, label: "Performance commitments", icon: Sparkles },
  { id: 3, label: "Growth milestones", icon: Calendar },
  { id: 4, label: "Employer support", icon: Handshake },
  { id: 5, label: "Review & activate", icon: CheckCircle },
];

const CASCADING_KEYS = [
  { agreementKey: "candidate_name", profileKey: "full_name", label: "Full Name" },
  { agreementKey: "location", profileKey: "location", label: "Location" },
];
const LOCKED_KEYS = [
  { agreementKey: "candidate_email", profileKey: "email", label: "Email" },
  { agreementKey: "candidate_phone", profileKey: "phone", label: "Phone" },
];

// Iter-37.2 — activation thresholds (mirrors backend /defaults).
const MIN_PERF = 3;
const MIN_MILESTONES = 2;
const MIN_EMP = 1;

// ─────────────────────────────────────────────────────────────────
// Module-level helper components. Defined OUTSIDE the parent so they
// keep stable references across parent re-renders (e.g. when autosave
// fires). If we inlined them inside the parent, every render would
// mint a new function reference and React would unmount/remount the
// Radix Select primitive — closing any open dropdown mid-click.
// ─────────────────────────────────────────────────────────────────

const LockedHint = () => (
  <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-slate-500 ml-2">
    <Lock className="h-3 w-3 mr-0.5" />
    from Profile
  </span>
);

// Category dropdown selector for commitments. Picking a category sets
// the row's label AND prefills the description with the category's
// `suggestion` if the row's description is currently empty.
const CategoryPicker = ({ options, value, onPick, testid }) => {
  const current = options.find((o) => o.label === value || o.category === value);
  return (
    <Select
      value={current?.category || ""}
      onValueChange={(cat) => {
        const opt = options.find((o) => o.category === cat);
        if (opt) onPick(opt);
      }}
    >
      <SelectTrigger className="w-full md:w-72 bg-white" data-testid={testid}>
        <SelectValue placeholder="Pick a category…" />
      </SelectTrigger>
      <SelectContent className="max-h-72 z-[60]">
        {options.map((o) => (
          <SelectItem key={o.category} value={o.category} data-testid={`${testid}-opt-${o.category}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const EMPTY = {
  candidate_name: "", candidate_email: "", candidate_phone: "",
  employer_name: "", employer_contact_name: "", employer_contact_title: "",
  employer_email: "", employer_phone: "",
  role_title: "", start_date: "", term_years: 1.0, location: "",
  performance_commitments: [], growth_milestones: [], employer_commitments: [],
  notes: "",
};

const SuccessAgreementBuilder = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [data, setData] = useState(EMPTY);
  const [defaults, setDefaults] = useState(null);
  const [profile, setProfile] = useState(null);

  // PDF preview (Step 5)
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewBlobRef = useRef(null);

  // Profile sync modal
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [pendingChangedFields, setPendingChangedFields] = useState([]);
  const [pendingNextStep, setPendingNextStep] = useState(null);
  const touchedFieldsRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [state, list, d, p] = await Promise.all([
        successAgreementAPI.getState(),
        successAgreementAPI.list(),
        successAgreementAPI.getDefaults(1.0),
        profileAPI.getProfile().catch(() => null),
      ]);
      setActive(state.active || null);
      setDefaults(d);
      setProfile(p);
      setHistory((list || []).filter((a) => a.state === "retired"));
      if (state.draft) {
        setDraftId(state.draft.id);
        setData({ ...EMPTY, ...state.draft });
      } else {
        setDraftId(null);
        setData({
          ...EMPTY,
          performance_commitments: (d.performance_commitments || []).map((c) => ({ ...c })),
          employer_commitments: (d.employer_commitments || []).map((c) => ({ ...c })),
          growth_milestones: (d.growth_milestones || []).map((m) => ({ ...m })),
        });
      }
    } catch (err) {
      console.error("agreement load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Profile cascade (iter-37.1) ─────────────────────────────────────────
  // On profile load, fill BLANK cascading fields and ALWAYS overwrite
  // locked fields (email + phone). User-edits to cascading fields stay.
  useEffect(() => {
    if (!profile) return;
    setData((prev) => {
      const next = { ...prev };
      CASCADING_KEYS.forEach(({ agreementKey, profileKey }) => {
        if (!next[agreementKey] && profile[profileKey]) {
          next[agreementKey] = profile[profileKey];
        }
      });
      LOCKED_KEYS.forEach(({ agreementKey, profileKey }) => {
        if (profile[profileKey]) next[agreementKey] = profile[profileKey];
      });
      return next;
    });
  }, [profile]);

  const computeChangedCascadingFields = useCallback(() => {
    if (!profile) return [];
    const out = [];
    CASCADING_KEYS.forEach(({ agreementKey, profileKey, label }) => {
      if (!touchedFieldsRef.current.has(agreementKey)) return;
      const docVal = (data[agreementKey] || "").toString().trim();
      const profVal = (profile[profileKey] || "").toString().trim();
      if (docVal && docVal !== profVal) {
        out.push({ key: agreementKey, profileKey, label, current: docVal, previous: profVal });
      }
    });
    return out;
  }, [data, profile]);

  const setField = (k, v, opts = {}) => {
    if (opts.userTouched !== false) {
      touchedFieldsRef.current.add(k);
    }
    setData((d) => ({ ...d, [k]: v }));
  };

  const handleTermChange = async (newTerm) => {
    setField("term_years", newTerm, { userTouched: false });
    try {
      const d = await successAgreementAPI.getDefaults(newTerm);
      const allEmpty = (data.growth_milestones || []).every((m) => !m.description);
      if (allEmpty) {
        setData((cur) => ({
          ...cur,
          term_years: newTerm,
          growth_milestones: (d.growth_milestones || []).map((m, i) => ({
            ...m,
            title: cur.growth_milestones?.[i]?.title || m.title,
          })),
        }));
      }
    } catch (_) { /* ignore */ }
  };

  const saveDraft = async (silent = false) => {
    setSaving(true);
    try {
      const saved = await successAgreementAPI.saveDraft(data);
      setDraftId(saved.id);
      setData((cur) => ({ ...EMPTY, ...saved, ...cur, ...saved }));
      if (!silent) toast.success("Draft saved.");
      return saved;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Iter-39 — debounced autosave for the Success Agreement draft.
  // Disabled while loading. We rely on shouldSave to skip empty seed
  // states so blank drafts aren't stub-saved.
  const autosaveSaver = useCallback(async (_d) => {
    const saved = await successAgreementAPI.saveDraft(_d);
    setDraftId(saved.id);
    setData((cur) => ({ ...EMPTY, ...saved, ...cur, ...saved }));
    return saved;
  }, []);

  const { status: autosaveStatus, lastSavedAt: autosaveAt, flush: autosaveFlush } = useAutosave(
    data,
    autosaveSaver,
    {
      delay: 1500,
      enabled: !loading,
      shouldSave: (d) => !!(d.candidate_name || d.employer_name || d.role_title),
    },
  );

  // Sync-modal aware advance: when user clicks Next, if any cascading
  // field diverges from Profile we open the 3-way modal first.
  const tryAdvance = async (nextStep) => {
    const changed = computeChangedCascadingFields();
    if (changed.length > 0) {
      setPendingChangedFields(changed);
      setPendingNextStep(nextStep);
      setSyncModalOpen(true);
      return;
    }
    // Iter-39 — flush autosave first so navigation reflects the latest
    // typing, then run the explicit save path saveDraft provides.
    try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
    await saveDraft(true);
    setStep(nextStep);
  };

  // Iter-37.4 — handleSyncChoice is now resilient: failures NEVER trap
  // the wizard. The modal stays open on failure so the user can retry,
  // pick "Change only this document" (which is independent of profile
  // sync), or Cancel out. Each branch handles its own errors locally
  // and reports a meaningful detail string rather than a generic
  // "Sync failed.".
  const handleSyncChoice = async (choice) => {
    if (syncBusy) return;
    // Cancel — always succeeds. Revert touched cascading fields back
    // to the profile values and close the modal.
    if (choice === "cancel") {
      const reverted = { ...data };
      pendingChangedFields.forEach(({ key, profileKey }) => {
        reverted[key] = (profile && profile[profileKey]) || "";
        touchedFieldsRef.current.delete(key);
      });
      setData(reverted);
      setSyncModalOpen(false);
      setPendingChangedFields([]);
      setPendingNextStep(null);
      return;
    }
    // Document-only — independent of profile sync. Save the draft.
    if (choice === "document") {
      setSyncBusy(true);
      try {
        await saveDraft(true);
        // Clear touched flags so the modal doesn't re-open on the
        // next Next click for these fields.
        pendingChangedFields.forEach(({ key }) => touchedFieldsRef.current.delete(key));
        setSyncModalOpen(false);
        if (pendingNextStep) {
          setStep(pendingNextStep);
          setPendingNextStep(null);
        }
        setPendingChangedFields([]);
        toast.success("Saved to this success agreement only.");
      } catch (err) {
        // Keep the modal open so the user can retry / cancel.
        const detail = err?.response?.data?.detail || err?.message || "Could not save draft.";
        toast.error(`Save failed — ${detail}. You can retry or pick another option.`);
      } finally {
        setSyncBusy(false);
      }
      return;
    }
    // Profile path — patch the profile, then save the draft. If the
    // patch succeeds but the save fails, we still report success on
    // the sync but keep the modal open so the user can pick another
    // path. If the patch itself fails we never touched the draft so
    // the user is free to retry.
    if (choice === "profile") {
      setSyncBusy(true);
      try {
        const patch = {};
        pendingChangedFields.forEach(({ key, profileKey }) => {
          patch[profileKey] = data[key];
        });
        // Step 1 — patch profile
        try {
          await profileAPI.syncFromDocument(patch);
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || "Could not update profile.";
          toast.error(
            `Profile sync failed — ${detail}. Try again, or pick "Change only this success agreement" to continue.`,
          );
          return;  // modal stays open
        }
        // Step 2 — save draft. Profile is already updated; if this
        // fails we tell the user but DON'T trap them.
        try {
          await saveDraft(true);
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || "Could not save draft.";
          toast.error(
            `Profile updated, but the agreement draft could not be saved — ${detail}. You can retry.`,
          );
          return;  // modal stays open so user can retry
        }
        // Step 3 — refresh local profile snapshot so future diffs are
        // accurate. Failure here is non-fatal: we keep the stale copy.
        try {
          const fresh = await profileAPI.getProfile();
          if (fresh) setProfile(fresh);
        } catch (_) { /* non-fatal */ }
        // Success — clear flags, close modal, advance.
        pendingChangedFields.forEach(({ key }) => touchedFieldsRef.current.delete(key));
        setSyncModalOpen(false);
        if (pendingNextStep) {
          setStep(pendingNextStep);
          setPendingNextStep(null);
        }
        setPendingChangedFields([]);
        toast.success("Profile and all documents updated.");
      } finally {
        setSyncBusy(false);
      }
    }
  };

  const canActivate = useMemo(
    () => {
      const filled = (items, ...keys) =>
        (items || []).filter((it) => keys.some((k) => (it?.[k] || "").trim())).length;
      const perfN = filled(data.performance_commitments, "description");
      const mileN = filled(data.growth_milestones, "description", "title");
      const empN = filled(data.employer_commitments, "description");
      return !!(
        data.candidate_name && data.employer_name && data.role_title
        && perfN >= MIN_PERF && mileN >= MIN_MILESTONES && empN >= MIN_EMP
      );
    },
    [data.candidate_name, data.employer_name, data.role_title,
     data.performance_commitments, data.growth_milestones, data.employer_commitments],
  );

  // Per-section content counts — used for inline guidance.
  const counts = useMemo(() => {
    const f = (items, ...keys) =>
      (items || []).filter((it) => keys.some((k) => (it?.[k] || "").trim())).length;
    return {
      perf: f(data.performance_commitments, "description"),
      mile: f(data.growth_milestones, "description", "title"),
      emp: f(data.employer_commitments, "description"),
    };
  }, [data.performance_commitments, data.growth_milestones, data.employer_commitments]);

  const handleActivate = async () => {
    if (!canActivate) { toast.error("Fill candidate, employer, and role first."); return; }
    setActivating(true);
    try {
      try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
      let id = draftId;
      if (!id) { const saved = await saveDraft(true); id = saved.id; }
      else { await saveDraft(true); }
      const activated = await successAgreementAPI.activate(id);
      toast.success("Agreement activated and signed.");
      setActive(activated);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not activate.");
    } finally {
      setActivating(false);
    }
  };

  const handleDownload = async (id) => {
    setDownloading(true);
    try {
      const blob = await successAgreementAPI.downloadPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `success-agreement-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  // Iter-37.3 — browser-proof preview helpers.
  // The Step-5 preview iframe is fragile across Chrome's blob:// URL
  // restrictions (especially on preview subdomains + cross-origin PDF
  // embeds). These two helpers give the user reliable escape hatches
  // independent of the iframe's behavior.
  const downloadPreview = async () => {
    setPreviewLoading(true);
    try {
      const blob = await successAgreementAPI.previewPdf(data);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `success-agreement-preview.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      // Revoke shortly after — some browsers race the click.
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast.success("Preview PDF downloaded.");
    } catch (_) {
      toast.error("Preview download failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreviewInNewTab = async () => {
    // Programmatic window.open during a click is more reliably treated
    // as a user gesture than an <a target="_blank" href="blob:...">.
    // We open the tab immediately (popup blocker friendly), then
    // navigate it once the blob URL is ready.
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Browser blocked the new tab. Try the Download button instead.");
      return;
    }
    try {
      const blob = await successAgreementAPI.previewPdf(data);
      const url = window.URL.createObjectURL(blob);
      win.location.href = url;
      // Hold the URL alive long enough for the new tab's PDF viewer
      // to finish loading. 60s is plenty for a ~50 KB PDF.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      try { win.close(); } catch (_) { /* noop */ }
      toast.error("Could not open preview.");
    }
  };

  // ── Step 5: live PDF preview ─────────────────────────────────────────────
  const refreshPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const blob = await successAgreementAPI.previewPdf(data);
      const url = window.URL.createObjectURL(blob);
      if (previewBlobRef.current) {
        window.URL.revokeObjectURL(previewBlobRef.current);
      }
      previewBlobRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      console.error("preview failed", err);
      toast.error("Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  }, [data]);

  // Auto-refresh preview when entering step 5 or when data changes while
  // on step 5 (debounced).
  useEffect(() => {
    if (step !== 5) return undefined;
    const t = setTimeout(() => { refreshPreview(); }, 400);
    return () => clearTimeout(t);
  }, [step, refreshPreview]);

  // Cleanup the object URL on unmount.
  useEffect(() => () => {
    if (previewBlobRef.current) {
      window.URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
  }, []);

  const startNew = async () => {
    if (active && !window.confirm("Starting a new agreement will retire your current active one when you activate it. The current agreement and its PDF are preserved forever. Continue?")) {
      return;
    }
    setDraftId(null);
    touchedFieldsRef.current.clear();
    // Iter-37.4: also seed candidate identity fields from Profile so the
    // fresh draft is not blank. Mirrors the load-time cascade effect —
    // locked fields (email/phone) overwrite blanks; cascading fields
    // (name/location) fill blanks. Without this the cascade `useEffect`
    // doesn't re-run because the `profile` dependency hasn't changed.
    const p = profile || {};
    setData({
      ...EMPTY,
      candidate_name: p.full_name || "",
      candidate_email: p.email || "",
      candidate_phone: p.phone || "",
      location: p.location || "",
      performance_commitments: (defaults?.performance_commitments || []).map((c) => ({ ...c })),
      employer_commitments: (defaults?.employer_commitments || []).map((c) => ({ ...c })),
      growth_milestones: (defaults?.growth_milestones || []).map((m) => ({ ...m })),
    });
    setStep(1);
  };

  const updateListItem = (field, idx, key, value) => {
    setData((d) => {
      const list = [...(d[field] || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...d, [field]: list };
    });
  };
  const addListItem = (field, blank) =>
    setData((d) => ({ ...d, [field]: [...(d[field] || []), blank] }));
  const removeListItem = (field, idx) =>
    setData((d) => ({ ...d, [field]: (d[field] || []).filter((_, i) => i !== idx) }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="agreement-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const pickPerformanceCategory = (idx, opt) => {
    setData((d) => {
      const list = [...(d.performance_commitments || [])];
      const row = { ...(list[idx] || {}) };
      row.category = opt.category;
      // For "Custom" we seed a friendly placeholder label that prints
      // cleanly in the PDF if the candidate doesn't edit it. Non-custom
      // picks set the label to the human-readable category name.
      row.label = opt.category === "custom" ? "Custom commitment" : opt.label;
      if (!row.description && opt.suggestion) row.description = opt.suggestion;
      list[idx] = row;
      return { ...d, performance_commitments: list };
    });
  };
  const pickEmployerCategory = (idx, opt) => {
    setData((d) => {
      const list = [...(d.employer_commitments || [])];
      const row = { ...(list[idx] || {}) };
      row.category = opt.category;
      row.label = opt.category === "custom" ? "Custom commitment" : opt.label;
      if (!row.description && opt.suggestion) row.description = opt.suggestion;
      list[idx] = row;
      return { ...d, employer_commitments: list };
    });
  };

  const perfCategoryOptions = defaults?.performance_category_options || [];
  const empCategoryOptions = defaults?.employer_category_options || [];

  return (
    <div className="space-y-8" data-testid="success-agreement-section">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Handshake className="h-7 w-7 text-blue-600" />
          Success Agreement
        </h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          A short, printable performance &amp; growth commitment between you and a specific employer.
          Pick a term (1-3 years), set out what you each commit to, sign, and bring a printed copy.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs text-blue-800 font-medium" data-testid="agreement-purpose-pill">
          <ShieldCheck className="h-3.5 w-3.5 text-blue-700" />
          A signed, mutual document — designed to help both sides hire, grow, and review with shared expectations.
        </div>
      </div>

      {active && (
        <Card data-testid="agreement-active-card" className="border-emerald-200 bg-emerald-50/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              Active agreement
            </CardTitle>
            <CardDescription>
              <span className="font-semibold text-slate-800">{active.employer_name}</span>
              {" · "}{active.role_title}{" · "}{active.term_years} yr
              {active.activated_at && <> · Activated {new Date(active.activated_at).toLocaleDateString()}</>}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => handleDownload(active.id)}
                disabled={downloading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="agreement-download-active-btn"
              >
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Download signed PDF
              </Button>
              <Button variant="outline" onClick={startNew} data-testid="agreement-new-btn">
                <Plus className="h-4 w-4 mr-2" />
                Start a new agreement
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="agreement-stepper">
        {STEPS.map((s) => {
          const Icon = s.icon; const current = s.id === step; const past = s.id < step;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setStep(s.id)}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all ${
                current ? "bg-blue-600 text-white" : past ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
              }`}
              data-testid={`agreement-step-${s.id}-btn`}
            >
              <Icon className="h-3.5 w-3.5" />
              {s.id}. {s.label}
            </button>
          );
        })}
        <div className="ml-auto pl-3">
          <AutosaveIndicator status={autosaveStatus} lastSavedAt={autosaveAt} />
        </div>
      </div>

      <Card data-testid={`agreement-step-${step}-card`}>
        <CardContent className="pt-6 space-y-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Parties & term
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Your name + contact details are pulled from <span className="font-semibold text-slate-700">Profile</span>. Email and phone are locked there — edit the Profile page to change them.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Candidate name</Label>
                  <Input
                    value={data.candidate_name}
                    onChange={(e) => setField("candidate_name", e.target.value)}
                    data-testid="agreement-candidate-name"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center">
                    Candidate email <LockedHint />
                  </Label>
                  <Input value={data.candidate_email} disabled readOnly className="bg-slate-50 text-slate-600 cursor-not-allowed" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center">
                    Candidate phone <LockedHint />
                  </Label>
                  <Input value={data.candidate_phone} disabled readOnly className="bg-slate-50 text-slate-600 cursor-not-allowed" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Location (city)</Label>
                  <Input value={data.location} onChange={(e) => setField("location", e.target.value)} data-testid="agreement-candidate-location" />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Employer</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Company name *</Label>
                    <Input value={data.employer_name} onChange={(e) => setField("employer_name", e.target.value, { userTouched: false })} data-testid="agreement-employer-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Hiring manager / supervisor</Label>
                    <Input value={data.employer_contact_name} onChange={(e) => setField("employer_contact_name", e.target.value, { userTouched: false })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Their title</Label>
                    <Input value={data.employer_contact_title} onChange={(e) => setField("employer_contact_title", e.target.value, { userTouched: false })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Contact email</Label>
                    <Input value={data.employer_email} onChange={(e) => setField("employer_email", e.target.value, { userTouched: false })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Contact phone</Label>
                    <Input value={data.employer_phone} onChange={(e) => setField("employer_phone", e.target.value, { userTouched: false })} />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Role & term</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Role title *</Label>
                    <Input value={data.role_title} onChange={(e) => setField("role_title", e.target.value, { userTouched: false })} data-testid="agreement-role-title" placeholder="e.g., Barista, Server" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Start date</Label>
                    <Input type="date" value={data.start_date || ""} onChange={(e) => setField("start_date", e.target.value, { userTouched: false })} data-testid="agreement-start-date" />
                  </div>
                </div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mt-4 block">Term length *</Label>
                <div className="grid grid-cols-5 gap-2 mt-2" data-testid="agreement-term-options">
                  {TERM_OPTIONS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => handleTermChange(t)}
                      className={`rounded-lg border p-3 text-sm font-semibold transition-all ${
                        data.term_years === t
                          ? "border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                      data-testid={`agreement-term-${t}`}
                    >
                      {t} yr
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" /> Performance commitments
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  What will <span className="font-semibold text-slate-700">you</span> commit to over the term? Pick a category to prefill a suggested statement, then edit it in your own words. Aim for at least <span className="font-semibold">{MIN_PERF} commitments</span> ({counts.perf}/{MIN_PERF} ready).
                </p>
              </div>
              {(data.performance_commitments || []).map((c, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`agreement-perf-${i}`}>
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                      <CategoryPicker
                        options={perfCategoryOptions}
                        value={c.category || c.label}
                        onPick={(opt) => pickPerformanceCategory(i, opt)}
                        testid={`agreement-perf-${i}-category`}
                      />
                      <Input
                        value={c.label || ""}
                        onChange={(e) => updateListItem("performance_commitments", i, "label", e.target.value)}
                        className="font-semibold text-slate-900 flex-1 min-w-[200px]"
                        placeholder="Category label"
                        data-testid={`agreement-perf-${i}-label`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeListItem("performance_commitments", i)} data-testid={`agreement-perf-${i}-remove`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                  <Textarea
                    value={c.description || ""}
                    onChange={(e) => updateListItem("performance_commitments", i, "description", e.target.value)}
                    placeholder="Pick a category above to load a suggested statement, or write your own."
                    rows={3}
                    data-testid={`agreement-perf-${i}-desc`}
                  />
                </div>
              ))}
              <Button variant="outline" onClick={() => addListItem("performance_commitments", { category: "custom", label: "", description: "" })} data-testid="agreement-perf-add">
                <Plus className="h-4 w-4 mr-2" /> Add another commitment
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" /> Growth milestones
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Checkpoints along the {data.term_years}-year term. Aim for at least <span className="font-semibold">{MIN_MILESTONES} milestones</span> with a title or description ({counts.mile}/{MIN_MILESTONES} ready).
                </p>
              </div>
              {(data.growth_milestones || []).map((m, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`agreement-milestone-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input value={m.when || ""} onChange={(e) => updateListItem("growth_milestones", i, "when", e.target.value)} placeholder="When" className="text-blue-700 font-semibold" />
                    <Input value={m.title || ""} onChange={(e) => updateListItem("growth_milestones", i, "title", e.target.value)} placeholder="Milestone title" />
                  </div>
                  <Textarea value={m.description || ""} onChange={(e) => updateListItem("growth_milestones", i, "description", e.target.value)} placeholder="e.g., Complete latte art training." rows={2} />
                  <div className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => removeListItem("growth_milestones", i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={() => addListItem("growth_milestones", { when: "", title: "", description: "" })}>
                <Plus className="h-4 w-4 mr-2" /> Add milestone
              </Button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Handshake className="h-5 w-5 text-blue-600" /> Employer support &amp; review commitments
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  What does <span className="font-semibold text-slate-700">the employer</span> commit to in return — training, feedback, reviews, growth? Pick a category to prefill a draft, then edit in their own words. Aim for at least <span className="font-semibold">{MIN_EMP} commitment</span> ({counts.emp}/{MIN_EMP} ready).
                </p>
              </div>
              {(data.employer_commitments || []).map((c, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`agreement-emp-${i}`}>
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                      <CategoryPicker
                        options={empCategoryOptions}
                        value={c.category || c.label}
                        onPick={(opt) => pickEmployerCategory(i, opt)}
                        testid={`agreement-emp-${i}-category`}
                      />
                      <Input
                        value={c.label || ""}
                        onChange={(e) => updateListItem("employer_commitments", i, "label", e.target.value)}
                        className="font-semibold text-slate-900 flex-1 min-w-[200px]"
                        placeholder="Category label"
                        data-testid={`agreement-emp-${i}-label`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeListItem("employer_commitments", i)} data-testid={`agreement-emp-${i}-remove`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                  <Textarea
                    value={c.description || ""}
                    onChange={(e) => updateListItem("employer_commitments", i, "description", e.target.value)}
                    placeholder="Pick a category above to load a suggested statement, or write your own."
                    rows={3}
                    data-testid={`agreement-emp-${i}-desc`}
                  />
                </div>
              ))}
              <Button variant="outline" onClick={() => addListItem("employer_commitments", { category: "custom", label: "", description: "" })} data-testid="agreement-emp-add">
                <Plus className="h-4 w-4 mr-2" /> Add another commitment
              </Button>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" /> Review & activate
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Preview the final signed PDF below. When you're happy, click <span className="font-semibold">Sign &amp; activate</span>.
                </p>
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <UserIcon className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold">{data.candidate_name || "—"}</span>
                  <span className="text-slate-500">→</span>
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold">{data.employer_name || "—"}</span>
                </div>
                <div className="text-slate-600">
                  Role: <span className="font-medium text-slate-800">{data.role_title || "—"}</span>
                  {" · "}Term: <span className="font-medium text-slate-800">{data.term_years} yr</span>
                  {data.start_date && <> · Starts {data.start_date}</>}
                </div>
              </div>

              {/* Iter-37.3: browser-proof live PDF preview */}
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50" data-testid="agreement-preview-container">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <Eye className="h-3.5 w-3.5" />
                    Live preview
                    {previewLoading && <Loader2 className="h-3 w-3 animate-spin text-blue-600" />}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Button size="sm" variant="ghost" onClick={refreshPreview} disabled={previewLoading} data-testid="agreement-preview-refresh-btn">
                      Refresh
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openPreviewInNewTab}
                      disabled={previewLoading}
                      data-testid="agreement-preview-open-tab-btn"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      Open in new tab
                    </Button>
                    <Button
                      size="sm"
                      onClick={downloadPreview}
                      disabled={previewLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="agreement-preview-download-btn"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Download preview
                    </Button>
                  </div>
                </div>
                {previewUrl ? (
                  // <object> wraps <iframe> + an inline fallback message so the
                  // PDF still loads even if Chrome's PDF viewer refuses to
                  // render the blob: URL on this preview origin. Both elements
                  // honor `type="application/pdf"` to nudge the native viewer.
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full bg-white block"
                    style={{ height: 760 }}
                    data-testid="agreement-preview-object"
                  >
                    <iframe
                      src={previewUrl}
                      title="Success Agreement preview"
                      className="w-full bg-white block"
                      style={{ height: 760, border: 0 }}
                      data-testid="agreement-preview-iframe"
                    />
                    <div className="p-6 text-center text-sm text-slate-600 bg-amber-50 border-t border-amber-200">
                      Your browser refused to render the PDF inline.
                      Use <span className="font-semibold">Download preview</span> or <span className="font-semibold">Open in new tab</span> to view the file.
                    </div>
                  </object>
                ) : (
                  <div className="p-8 text-center text-sm text-slate-500">
                    <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin text-blue-600" />
                    Generating preview…
                  </div>
                )}
                <div className="px-3 py-2 border-t border-slate-200 bg-white text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="agreement-preview-hint">
                  <AlertCircle className="h-3 w-3 text-slate-400" />
                  If the inline preview looks blank, your browser is blocking the embed —
                  <span className="font-semibold ml-1">Download preview</span> always works.
                </div>
              </div>

              {!canActivate && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="agreement-cannot-activate-hint">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <div className="font-semibold">A bit more content before activation:</div>
                    <ul className="list-disc list-inside text-amber-800/90">
                      {!data.candidate_name && <li>Candidate name (step 1)</li>}
                      {!data.employer_name && <li>Employer name (step 1)</li>}
                      {!data.role_title && <li>Role title (step 1)</li>}
                      {counts.perf < MIN_PERF && <li>At least {MIN_PERF} performance commitments ({counts.perf}/{MIN_PERF})</li>}
                      {counts.mile < MIN_MILESTONES && <li>At least {MIN_MILESTONES} growth milestones ({counts.mile}/{MIN_MILESTONES})</li>}
                      {counts.emp < MIN_EMP && <li>At least {MIN_EMP} employer support commitment ({counts.emp}/{MIN_EMP})</li>}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => saveDraft(false)} variant="outline" disabled={saving} data-testid="agreement-save-draft-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save draft
                </Button>
                <Button
                  onClick={handleActivate}
                  disabled={!canActivate || activating}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="agreement-activate-btn"
                >
                  {activating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Sign & activate
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-between border-t border-slate-100 pt-4 mt-4">
            <Button variant="outline" onClick={async () => { try { await autosaveFlush(); } catch (_) {} setStep((s) => Math.max(1, s - 1)); }} disabled={step === 1} data-testid="agreement-prev-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < 5 ? (
              <Button onClick={() => tryAdvance(step + 1)} data-testid="agreement-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <div />
            )}
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card data-testid="agreement-history-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Past agreements ({history.length})</CardTitle>
            <CardDescription>Retired agreements are kept forever.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-100">
              {history.map((h) => (
                <li key={h.id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`agreement-history-row-${h.id}`}>
                  <div>
                    <div className="font-semibold text-slate-800">{h.employer_name || "—"}</div>
                    <div className="text-xs text-slate-500">
                      {h.role_title} · {h.term_years} yr · Retired {h.retired_at ? new Date(h.retired_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleDownload(h.id)}>
                    <Download className="h-4 w-4 mr-1.5" /> Re-download PDF
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <ProfileSyncModal
        open={syncModalOpen}
        changedFields={pendingChangedFields}
        documentLabel="Success Agreement"
        busy={syncBusy}
        onClose={() => handleSyncChoice("cancel")}
        onChooseDocumentOnly={() => handleSyncChoice("document")}
        onChooseSyncAll={() => handleSyncChoice("profile")}
      />
    </div>
  );
};

export default SuccessAgreementBuilder;
